import { fetchApi } from './client';
import { auth } from '@/lib/firebase/config';

// Receipt is a direct authenticated PDF stream (not a redirect to a hosted
// gateway invoice URL like getOrderInvoiceUrl), so it needs a blob download
// with an auth header rather than window.open — mirrors downloadCertificatePdf
// in certificates.ts.
async function downloadOrderReceiptPdf(orderId: string, filename: string): Promise<void> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`/api/proxy/payment/orders/${orderId}/receipt`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to download receipt');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export type PaymentInstructions =
  // receiverName: the bank-app-facing account name a real BRImo/mobile-banking receipt
  // shows next to the VA number (e.g. "PTAKSAIN Aksademia") — optional because older/
  // cached instructions payloads predate this field.
  | { type: 'VA'; bank: string; vaNumber: string; receiverName?: string }
  | { type: 'QRIS'; qrString: string; qrImageUrl?: string }
  | { type: 'EWALLET'; provider: string; deeplinkUrl?: string; qrImageUrl?: string };

export interface PurchaseResponse {
  id: string;
  userId: string;
  testModuleId: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'EXPIRED';
  gateway: string;
  gatewayInvoiceId: string | null;
  gatewayPaymentUrl: string | null;
  orderId: string | null;
  amount: number;
  currency: string;
  refundedAmount: number;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Snapshot of the product title/kind at purchase time — survives the
  // product being soft-deleted later. Null on legacy pre-snapshot rows.
  productTitle: string | null;
  productKind: 'module' | 'course' | 'bundle' | null;
}

export interface PaymentMethodResponse {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  gateway: string;
  iconUrl: string | null;
  sortOrder: number;
  minAmount: number | null;
  maxAmount: number | null;
  feeFlat: number;
  feePercent: number;
  isActive: boolean;
}

export interface AccessCheckResponse {
  hasAccess: boolean;
}

export interface OwnedItemsResponse {
  moduleIds: string[];
  courseIds: string[];
  bundleIds: string[];
}

export interface CreateOrderResponse {
  orderId: string;
  status: string;
  paymentUrl: string | null;
  expiresAt: string | null;
  subtotalAmount: number;
  discountAmount: number;
  // Payment-method surcharge, already folded into totalAmount below — exposed
  // separately so a confirmation/receipt view can show the same "Biaya" row
  // the cart did. Null for a free order (no payment method involved).
  feeAmount?: number | null;
  totalAmount: number;
  currency: string;
  purchaseCount: number;
  // Payment processor for this order, shown in the VA destination block. Not a
  // constant — see PaymentInstructions.tsx's `processor` prop.
  gateway?: string | null;
  paymentInstructions?: PaymentInstructions | null;
  // Server-truth bank/gateway identity for the VA destination block — hydrated from the
  // same Order.paymentMethod relation getOrder returns, available immediately after
  // checkout starts rather than only after a later getOrder poll.
  paymentMethod?: { id: string; name: string; iconUrl: string | null } | null;
}

export interface ValidateDiscountResponse {
  valid: boolean;
  code?: string;
  label?: string;
  type?: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value?: number;
  discountAmount?: number;
  reason?: string;
}

export interface OrderResponse {
  id: string;
  // Human-facing identifier ("AKS-2026-000042") — prefer this over `id` (the
  // internal UUID) for anything shown to the user. Nullable only for the
  // narrow pre-backfill window; falls back to `id` at the display site.
  orderNumber: string | null;
  userId: string;
  status: PurchaseResponse['status'];
  // True when this FAILED order carries a USER_CANCELLED OrderEvent — i.e. the
  // buyer deliberately cancelled via POST /orders/:id/cancel, not a gateway-side
  // deny/failure. Only meaningful when status is FAILED. See
  // deriveCancelledByUser (apps/api) for the derivation.
  cancelledByUser?: boolean;
  gateway: string;
  gatewayPaymentUrl: string | null;
  paymentMethod: { id: string; name: string; code: string; iconUrl: string | null } | null;
  // Money breakdown — getOrder's raw Order row already carried these fields
  // over the wire (they're plain Order scalars/relations), this type just
  // hadn't declared them yet. subtotalAmount/discountAmount are non-null on
  // every order (DB default 0); feeAmount is null on legacy pre-#466 orders
  // and on the free/MANUAL-gateway instant-grant path — see
  // CreateOrderResponse.feeAmount's comment, same "already folded into
  // totalAmount" contract.
  subtotalAmount: number;
  discountAmount: number;
  discountCode?: { code: string } | null;
  feeAmount?: number | null;
  totalAmount: number;
  currency: string;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  purchases: Array<{
    id: string;
    testModuleId: string | null;
    courseId: string | null;
    bundleId: string | null;
    testModule: { id: string; title: string; slug: string | null } | null;
    course: { id: string; title: string; slug: string | null } | null;
    bundle: { id: string; title: string; slug: string | null } | null;
    amount: number;
    status: string;
    // Snapshot of the product title/kind at purchase time — see PurchaseResponse.
    productTitle: string | null;
    productKind: 'module' | 'course' | 'bundle' | null;
  }>;
  paymentInstructions?: PaymentInstructions | null;
}

// Response of the manual "force-check DOKU now" endpoint — the full order (same shape
// as getOrder's OrderResponse) plus whether the gateway check actually changed anything.
// `changed: false` is the common case (still PENDING, or the order was a no-op target —
// already terminal, or never had a gateway invoice) and must be handled as normal
// feedback, not an error.
export interface RefreshOrderResponse extends OrderResponse {
  changed: boolean;
}

// Response of POST /payment/orders/:id/cancel. `cancelled: false` is not an
// error — it means the live gateway check found the order already settled
// (e.g. PAID) and fulfilled it through the normal pipeline instead of
// cancelling; `order` reflects that real outcome either way. A
// CANCEL_FAILED_TRY_AGAIN / CANCEL_UNSUPPORTED / ORDER_NOT_CANCELLABLE outcome
// throws ApiError instead (see fetchApi) — the order is guaranteed to still be
// PENDING in that case.
export interface CancelOrderResponse {
  cancelled: boolean;
  order: OrderResponse;
}

// SSE must bypass the Next proxy (which buffers); hit the API origin directly.
export function paymentStreamUrl(apiBaseUrl: string, orderId: string, idToken: string): string {
  return `${apiBaseUrl}/v1/payment/orders/${orderId}/stream?token=${encodeURIComponent(idToken)}`;
}

// Shared envelope for the opt-in server-pagination endpoints. Absent a `page`
// param these same endpoints still return a bare array (see getOrders/getPurchases
// below) — Paginated<T> only describes the shape returned once `page` is sent.
export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface HistoryQueryParams {
  page: number;
  pageSize?: number;
  status?: string;
  q?: string;
  from?: string;
  to?: string;
  // Server excludes EXPIRED rows only when set AND no explicit `status` is
  // given — an explicit status always wins. Harmless to send both.
  excludeExpired?: boolean;
}

function buildHistoryQuery(params: HistoryQueryParams): string {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.status) query.set('status', params.status);
  if (params.q) query.set('q', params.q);
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.excludeExpired) query.set('excludeExpired', 'true');
  return query.toString();
}

// A PENDING order whose deadline has already passed is effectively EXPIRED —
// the gateway rejects payment past it. The backend flips the row to EXPIRED
// lazily (webhook/cron), so the client applies the same rule to stay consistent
// the moment the deadline lapses. Single source of truth for the payments list
// and the order-detail page — keep them in sync by importing this, not copies.
export function isPaymentClientExpired(status: string, expiresAt: string | null): boolean {
  return status === 'PENDING' && expiresAt != null && new Date(expiresAt) < new Date();
}

export const paymentApi = {
  // `amount`, when passed, filters out methods whose minAmount/maxAmount band doesn't
  // admit this order — UX only, the server enforces the same rule again at charge time.
  getPaymentMethods: (amount?: number): Promise<PaymentMethodResponse[]> =>
    fetchApi(`/payment/methods${amount !== undefined ? `?amount=${amount}` : ''}`),

  // Order-based (cart)
  createOrder: (params: {
    items: Array<{ testModuleId?: string; courseId?: string; bundleId?: string; eventId?: string }>;
    idempotencyKey: string;
    paymentMethodCode?: string;
    discountCode?: string;
    successRedirectUrl?: string;
    failureRedirectUrl?: string;
  }): Promise<CreateOrderResponse> =>
    fetchApi('/payment/orders', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  validateDiscountCode: (params: {
    code: string;
    items: Array<{ testModuleId?: string; courseId?: string; bundleId?: string; eventId?: string }>;
  }): Promise<ValidateDiscountResponse> =>
    fetchApi('/payment/discount-codes/validate', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getOrders: (): Promise<OrderResponse[]> =>
    fetchApi('/payment/orders'),

  // Opt-in server pagination — passing `page` flips the endpoint from the bare
  // array above to the { data, total, page, pageSize, totalPages } envelope.
  // Does not replace getOrders(): tests/page.tsx and courses/page.tsx still need
  // the full array for ownership computation.
  getOrdersPage: (params: HistoryQueryParams): Promise<Paginated<OrderResponse>> =>
    fetchApi(`/payment/orders?${buildHistoryQuery(params)}`),

  getOrder: (orderId: string): Promise<OrderResponse> =>
    fetchApi(`/payment/orders/${orderId}`),

  // Manual "Cek Status" — force-checks the gateway for just this order, bypassing the
  // 60s reconciliation sweep's 2-minute floor. Same auth (httpOnly aks_session cookie via
  // fetchApi) as getOrder above — no Firebase bearer token involved, unlike the SSE
  // stream URL below, which needs the token as a query param because EventSource can't
  // send custom headers. Rate-limited server-side (1 call / 12s / user+order) — a 429
  // throws an ApiError whose `details` carries `retryAfterSeconds`.
  refreshOrderStatus: (orderId: string): Promise<RefreshOrderResponse> =>
    fetchApi(`/payment/orders/${orderId}/refresh`, { method: 'POST' }),

  // "Batalkan Pesanan" — only ever shown for a still-PENDING order whose gateway
  // supports a safe cancel (see CartDrawer's activeOrder.gateway === 'MIDTRANS'
  // check). Rate limited server-side to 1 call per 12s per user+order, same
  // shape as refreshOrderStatus.
  cancelOrder: (orderId: string): Promise<CancelOrderResponse> =>
    fetchApi(`/payment/orders/${orderId}/cancel`, { method: 'POST' }),

  getOrderInvoiceUrl: (orderId: string): Promise<{ invoiceUrl: string }> =>
    fetchApi(`/payment/orders/${orderId}/invoice`),

  // orderNumber (when the caller has it) drives the downloaded filename — a
  // blob: URL download ignores the response's Content-Disposition header
  // entirely, so the human-facing name has to be set client-side here, not
  // just on the API response. Falls back to the UUID fragment only for the
  // narrow pre-backfill case where orderNumber isn't available yet.
  getOrderReceipt: (orderId: string, orderNumber?: string | null): Promise<void> =>
    downloadOrderReceiptPdf(orderId, `Kwitansi-${orderNumber || orderId.slice(0, 8)}.pdf`),

  // Legacy single-purchase — READS ONLY. There is no createPurchase here: the
  // server route was deleted 2026-07-29 (see apps/api payment.routes.ts) and
  // this client function had zero call sites in any frontend. Checkout goes
  // through createOrder above, which is the only path the orphan-VA sweeper
  // can see.
  getPurchases: (): Promise<PurchaseResponse[]> =>
    fetchApi('/payment/purchases'),

  // Same opt-in pagination contract as getOrdersPage — see its comment above.
  getPurchasesPage: (params: HistoryQueryParams): Promise<Paginated<PurchaseResponse>> =>
    fetchApi(`/payment/purchases?${buildHistoryQuery(params)}`),

  getPurchase: (purchaseId: string): Promise<PurchaseResponse> =>
    fetchApi(`/payment/purchases/${purchaseId}`),

  // Orphan purchases (orderId === null) predating the Order system — a small,
  // bounded, non-growing set, so always a full bare array, never paginated.
  // Rows carry productTitle/productKind/amount/status/createdAt directly
  // (no relations to walk, unlike OrderResponse.purchases).
  getLegacyPurchases: (): Promise<PurchaseResponse[]> =>
    fetchApi('/payment/purchases/legacy'),

  checkAccess: (testModuleId: string): Promise<AccessCheckResponse> =>
    fetchApi(`/payment/access/${testModuleId}`),

  // Same endpoint landing-page's payments API already calls for its
  // OwnedItemsContext — used here by CartContext to reconcile the cart
  // against ownership on load, so a module the user already bought (in
  // another tab, another device, or via an async gateway) doesn't keep
  // sitting in the cart and inflating the header badge.
  getOwnedItems: (): Promise<OwnedItemsResponse> =>
    fetchApi('/payment/owned'),
};
